import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import DialogBox from '../../../core-components/theme/components/DialogBox/DialogBox';
import ApiBasedAutoComplete from '../../../globalComponents/apiBasedAutoComplete';
import Table from '../../../core-components/theme/components/Table/Table';
import SaveCancelButtons from '../../../core-components/theme/components/SaveCancelComponent/SaveCancelButtons';
import { useTriggerPlantAlarm } from '../hooks/useTriggerPlantAlarm';
import { SCADA_RULES, useEnforcedPlantFilter } from '../../../access';
import { usePlantAlarmSSE } from '../hooks/usePlantAlarmSSE';
import Loader from '../../../globalComponents/loader/DPLoader';

interface IViewAsProps {
  open: boolean;
  handleClose: () => void;
}

interface IPlant {
  _id: string;
  plantName: string;
}

function TriggerPlantAlarmDialog({ open, handleClose }: IViewAsProps) {
  const { t } = useTranslation();

  const [selectedPlant, setSelectedPlant] = useState<IPlant | null>(null);
  const [bidirectionalLogId, setBidirectionalLogId] = useState<string>('');
  const [dataLoggerId, setDataLoggerId] = useState<string>('');

  const { triggerAlarm, isTriggering } = useTriggerPlantAlarm();
  const isActuationPlant = useEnforcedPlantFilter(SCADA_RULES.actuate);
  const { events, isWaiting, resetEvents, isIncomingData } = usePlantAlarmSSE(open, dataLoggerId, selectedPlant, bidirectionalLogId);



  const isSaveDisabled = (() => {
    if(!selectedPlant) return true;
    if ( !events || events?.length === 0) return false;
    if (!isIncomingData) return false;

    const matchingEvents = events?.filter(e => e?.plantName === selectedPlant?.plantName);

    if (matchingEvents?.length === 0) return false;

    const latestEvent = matchingEvents[matchingEvents?.length - 1];

    return latestEvent?.controlValue;
  })();

  const handleSubmit = () => {
    if (selectedPlant?._id) {
      triggerAlarm(
        { plantId: selectedPlant._id },
        {
          onSuccess: data => {
            resetEvents();
            setBidirectionalLogId(data?.data?.bidirectionalLogId);
            setDataLoggerId(data?.data?.dataLoggerId);
          },
          onError: (err: any) => {
            toast.error(err?.message || t('plantAlarmTrigger.failedToTrigger', 'Failed to trigger alarm'));
          },
        }
      );
    } else {
      toast.error(t('plantAlarmTrigger.selectPlantFirst', 'Please select a plant first.'));
    }
  };

  const columns: any = [
    {
      field: 'plantName',
      headerName: t('plantAlarmTrigger.columns.plantName', 'Plant Name'),
      renderCell: (value: boolean) => (
        <Typography variant="body">{value}</Typography>
      ),
    },
    {
      field: 'controlValue',
      headerName: t('plantAlarmTrigger.columns.alarmStatus', 'Alarm Status'),
      align: 'center',
      renderCell: (value: boolean) => (
        <Typography variant="body">{value ? t('plantAlarmTrigger.on', 'ON') : t('plantAlarmTrigger.off', 'OFF')}</Typography>
      ),
    },
    {
      field: 'countdown',
      headerName: t('plantAlarmTrigger.columns.alarmCountdown', 'Alarm Countdown'),
      align: 'center',
      renderCell: (value: boolean) => (
        <Typography variant="body">{value}</Typography>
      ),
    },
  ];

  return (
    <Box>
      <DialogBox
        open={open}
        onClose={handleClose}
        title={t('plantAlarmTrigger.title', 'Trigger Alarm')}
        padding="20px"
        contentPadding="20px 0px 10px 0px"
        minWidth="30%"
      >
        <ApiBasedAutoComplete
          onSelect={(newValue: IPlant | null) => {
             setSelectedPlant(newValue)
             setDataLoggerId('');
             setBidirectionalLogId('');
             }}
          apiPath={'/biDirectionalControl/getPlantsForBidirectional'}
          canOffer={(plant: IPlant) => isActuationPlant(plant._id)}
          searchKey="plantName"
          keyToPick="plantName"
          idKey="_id"
          label={t('plantAlarmTrigger.selectPlant', 'Select Plant')}
          selectedOptions={selectedPlant}
        />

        <SaveCancelButtons
          onSave={handleSubmit}
          customSaveDisabled={isSaveDisabled || isWaiting }
          onCancel={handleClose}
          saveValue={t('plantAlarmTrigger.trigger', 'TRIGGER')}
          cancelValue={t('plantAlarmTrigger.close', 'CLOSE')}
          loading={isTriggering}
        />

        {(isWaiting && !events?.[0]?.error ) && (
          <Box mt={4} display="flex" justifyContent="center" sx={{maxHeight: "100px"}}>
            <Loader height='100px'/>
          </Box>
        )}

        {(events?.length > 0 && !events?.[0]?.error && isIncomingData) && (
          <Box mt={3} mb={4}>
            <Typography variant="h3" mb={2}>
              {t('plantAlarmTrigger.liveAlarmStatus', 'Live Alarm Status')}
            </Typography>
            <Table columns={columns} rows={events} />
          </Box>
        )}
      </DialogBox>
    </Box>
  );
}

export default TriggerPlantAlarmDialog;
